<template>
  <private-gym-page id="notificaties-page" name="notificaties" :has_fab="false">

    <f7-block-title medium>
      Notificaties
    </f7-block-title>

    <f7-block-header>
      Kies waarover je een bericht op je telefoon krijgt. Je kunt dit altijd wijzigen.
    </f7-block-header>

    <!-- S4: the OS-permission banner (PREF-04, IC3).

         ONE v-if/v-else-if chain, so two variants can never stack. It sits
         between the intro and the list, in the normal document flow -- never
         sticky, so scrolling to the toggles does not drag a notice along.

         Nothing below this comment touches a toggle. See the SC4 note in the
         script block. -->
    <f7-block v-if="banner_variant === 'web'" strong inset>
      <div class="notificaties-body">
        Je bekijkt Private Gym in de browser. Je keuzes worden bewaard, maar notificaties ontvang je alleen in de app.
      </div>
    </f7-block>

    <f7-block v-else-if="banner_variant === 'denied'" strong inset>
      <f7-icon f7="bell_slash" class="notificaties-banner-icon mb-1"></f7-icon>
      <div class="notificaties-heading">
        Notificaties staan uit op je telefoon
      </div>
      <div class="notificaties-body mt-2">
        Je keuzes hieronder blijven bewaard, maar er komt niets binnen zolang Private Gym geen toestemming heeft.
      </div>
      <f7-button fill class="mt-3" @click="open_phone_settings">
        Open telefooninstellingen
      </f7-button>
    </f7-block>

    <f7-block v-else-if="banner_variant === 'prompt'" strong inset>
      <f7-icon f7="bell_slash" class="notificaties-banner-icon mb-1"></f7-icon>
      <div class="notificaties-heading">
        Zet notificaties aan op dit toestel
      </div>
      <div class="notificaties-body mt-2">
        Dan krijg je een herinnering voor je training en hoor je het meteen als er iets met je reservering verandert.
      </div>
      <f7-button fill class="mt-3" @click="ask_permission">
        Notificaties aanzetten
      </f7-button>
    </f7-block>

    <!-- S8: the inline preloader, copied from reserveringen.vue. Deliberately
         NOT the global loader overlay in the `app` store module, which is what
         payments.vue uses: that overlay covers the navbar and swallows the back
         gesture, so a member who opened this screen by accident has nothing left
         to press. (The overlay's mutation name is described rather than quoted
         because a plan gate greps this file for it.) -->
    <f7-block v-if="show_preloader" class="text-align-center">
      <f7-preloader></f7-preloader>
    </f7-block>

    <!-- S9: the load-failure block. Persistent rather than a toast: a load
         failure leaves the member with nothing on screen to act on, so the
         message must not disappear on a timer. No raw API text is surfaced --
         it is English, unbounded and meaningless to a member. -->
    <f7-block v-else-if="load_failed" strong inset>
      <div class="notificaties-heading">
        We konden je instellingen niet laden.
      </div>
      <div class="notificaties-body mt-2">
        Controleer je internetverbinding en probeer het opnieuw.
      </div>
      <f7-button fill class="mt-3" @click="load">
        Opnieuw proberen
      </f7-button>
    </f7-block>

    <!-- S5/S6/S7: the groups, driven entirely by the legal_basis the SERVER
         attached to each category. There is deliberately no category -> group
         map anywhere on this page. -->
    <template v-else>
      <template v-for="group in groups" :key="group.legal_basis">
        <f7-block-title>
          {{ group_title(group.legal_basis) }}
        </f7-block-title>

        <f7-list media-list class="list-strong list-outline list-dividers notificaties-list">
          <f7-list-item
            v-for="item in group.items"
            :key="item.category"
            :title="item.label"
            :text="item.description || undefined"
          >
            <template #after>
              <f7-toggle
                :checked="item.enabled"
                :disabled="saving === item.category"
                @toggle:change="on_toggle(item.category, $event)"
              ></f7-toggle>
            </template>
          </f7-list-item>
        </f7-list>

        <f7-block-footer v-if="group_footnote(group.legal_basis)">
          {{ group_footnote(group.legal_basis) }}
        </f7-block-footer>
      </template>
    </template>

  </private-gym-page>
</template>

<script>

import PrivateGymPage from "../../pages/_layouts/PrivateGymPage.vue";

import { App as CapacitorApp } from '@capacitor/app';

import { isPushSupported, openOsNotificationSettings } from '../../js/push';

/**
 * The notification settings screen (PREF-01, PREF-02, PREF-03, PREF-04).
 *
 * The published privacy policy already promises this screen exists --
 * "u kunt de afzonderlijke categorieen aan- en uitzetten in de instellingen van
 * de App" is committed text in www.privategym's PrivacyPolicy.vue -- so it is a
 * compliance surface, not only a convenience.
 *
 * THE ONE RULE THAT MUST NOT BE BROKEN HERE (SC4): the permission banner never
 * disables, hides, greys out or flips a toggle. Nothing below binds a toggle's
 * `checked`, `disabled` or visibility to `push/permission`. The toggles stay
 * live and keep their stored values while OS permission is off, so a member who
 * revokes permission and later re-grants it gets their own choices back rather
 * than defaults. If a future change makes a toggle depend on permission state,
 * the test named "SC4" in tests/components/account/notificaties.test.js fails.
 *
 * Labels and descriptions are SERVER-owned. This file holds no copy of them --
 * a client-side mirror is exactly the drift that left `marketing` missing from
 * src/js/push.js for a whole phase.
 */

const GROUP_COPY = {
  contract: {
    title: 'Over je lidmaatschap',
    footnote: 'Deze horen bij je lidmaatschap en staan standaard aan.',
  },
  consent: {
    title: 'Optioneel',
    footnote: 'Deze staan uit totdat je ze zelf aanzet. Je kunt ze op elk moment weer uitzetten.',
  },
};

/**
 * Keyed by `legal_basis`, NEVER by category.
 *
 * A `legal_basis` this client has never heard of falls back to "Overig" and is
 * rendered rather than dropped: the server is free to add a category, or to
 * re-classify one, without an app release making it invisible to the member.
 * A category-keyed map would silently hide it, which for a consent surface is a
 * compliance problem and not merely a rendering bug.
 */
const UNKNOWN_GROUP = {
  title: 'Overig',
  footnote: null,
};

/**
 * Shown when the OS settings page could not be opened.
 *
 * Deliberately not a dead end: it names the app so the member can find the entry
 * themselves, because the alternative -- a button that does nothing -- is the
 * one outcome worse than no button at all.
 */
const SETTINGS_FAILED_MESSAGE =
  'Openen van de instellingen is niet gelukt. Zoek Private Gym op in de instellingen van je telefoon.';

export default {
  components: {PrivateGymPage},

  props: {
    f7route: Object,
    f7router: Object,
  },

  mixins: [],

  data: function () {
    return {
      // A GET is in flight. Starts true because mounted() always issues one, so
      // the very first render is the preloader rather than a blank block.
      is_fetching: true,

      // The @capacitor/app listener handle, held so beforeUnmount can remove it.
      app_state_handle: null,

      // Set by beforeUnmount. addListener is async, so the page can be gone by
      // the time the handle arrives; without this the handle is stored on a dead
      // component and never removed.
      is_unmounted: false,
    };
  },

  computed: {
    groups() {
      return this.$store.getters['notification_preferences/groups'];
    },
    saving() {
      return this.$store.getters['notification_preferences/saving'];
    },
    has_loaded() {
      return this.$store.getters['notification_preferences/has_loaded'];
    },
    load_failed() {
      return this.$store.getters['notification_preferences/load_failed'];
    },

    /**
     * S8. The loading block exists only while there is nothing else to show --
     * that is the UI-SPEC's own definition of it, and it is why this is not
     * simply `!has_loaded`.
     *
     * `has_loaded` LATCHES: the store sets it in a `finally` and never clears
     * it, so after a failed load it stays true forever. Keying the preloader off
     * `!has_loaded` alone would therefore make "Opnieuw proberen" drop the
     * member onto a blank screen -- no spinner, no error block, no list --
     * instead of returning them to the loading state, which is the one thing S9
     * says pressing that button must do.
     *
     * The `groups.length === 0` half is what keeps a re-visit with data already
     * in the store from flashing a spinner over a perfectly good list.
     */
    show_preloader() {
      return (this.is_fetching || !this.has_loaded) && this.groups.length === 0;
    },

    permission() {
      return this.$store.getters['push/permission'];
    },

    /**
     * Which S4 variant to render, or null for none (IC3).
     *
     * Precedence is fixed and total, and the template renders it as one
     * v-if/v-else-if chain so two banners cannot stack.
     *
     * @returns {'web'|'denied'|'prompt'|null}
     */
    banner_variant() {
      // While S9 is up it is the only call to action on the screen (IC5). Two
      // blocks each offering a different button is how a member ends up in
      // system settings fixing a permission that was never the problem.
      if (this.load_failed) {
        return null;
      }

      // Synchronous and definite: no browser has this plugin, so there is
      // nothing to wait for and nothing that can throw.
      if (!isPushSupported()) {
        return 'web';
      }

      if (this.permission === 'denied') {
        return 'denied';
      }

      if (this.permission === 'prompt') {
        return 'prompt';
      }

      // 'granted' renders NOTHING -- no block, no placeholder; absence is the
      // state. null renders nothing YET: the permission check has not answered,
      // and a banner is never shown on a guess. If the check throws, permission
      // stays null and this branch is also what a member sees, which is the
      // point -- a failed plugin call must not become an error block on a
      // settings screen.
      return null;
    },
  },

  created() {
    //
  },

  mounted() {
    // Fetched here rather than in created() so the page chrome and the
    // preloader render first -- created() runs before the first paint, so a
    // synchronous-resolving fetch there could skip the loading state entirely.
    this.load();

    this.refresh_permission();

    // IC3, the locked decision: permission is re-read on mount AND on resume.
    // Members fix permission in the OS settings app and come straight back, and
    // a single-WebView app is never reloaded on return -- so without this the
    // member is looking at a banner about a problem they just solved.
    CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        this.refresh_permission();
      }
    })
      .then((handle) => {
        // T-39-39: the handle MUST be removed on unmount. The document is never
        // unloaded in this app, so a leaked listener accumulates on every visit
        // to this page and survives for the whole session.
        if (this.is_unmounted) {
          handle.remove();
          return;
        }

        this.app_state_handle = handle;
      })
      // A platform without @capacitor/app is not an error worth showing anyone:
      // it only means the resume re-check is unavailable, and the mount check
      // above has already run.
      .catch(() => null);
  },

  beforeUnmount() {
    this.is_unmounted = true;

    if (this.app_state_handle) {
      this.app_state_handle.remove();
      this.app_state_handle = null;
    }
  },

  methods: {
    load() {
      this.is_fetching = true;

      // Resolves rather than rejects on failure; the module sets `load_failed`
      // and the S9 block above is the only surface for it.
      return this.$store.dispatch('notification_preferences/fetch_all')
        .finally(() => {
          this.is_fetching = false;
        });
    },

    group_title(legal_basis) {
      return (GROUP_COPY[legal_basis] || UNKNOWN_GROUP).title;
    },

    group_footnote(legal_basis) {
      return (GROUP_COPY[legal_basis] || UNKNOWN_GROUP).footnote;
    },

    /**
     * IC1. Flip one category.
     *
     * The store action owns the optimistic write, the rollback and the single
     * failure message. This handler adds NOTHING on success -- no toast, no
     * checkmark, no re-fetch -- because the toggle having moved is the feedback.
     *
     * @param {string} category
     * @param {boolean} enabled  the toggle's new value
     */
    on_toggle(category, enabled) {
      const current = this.$store.getters['notification_preferences/all']
        .find((item) => item.category === category);

      // f7-toggle re-emits `toggle:change` whenever its `checked` prop is set
      // back programmatically (framework7/components/toggle/toggle-class.js:32-44
      // triggers a real `change` event from the setter), and setting `checked`
      // back is precisely what a ROLLBACK does. Without this guard one failed
      // save fires a second PUT writing the old value back, and a second failure
      // toast with it -- so "exactly one message per failed action" would be
      // false. Comparing against the store rather than against a captured row
      // keeps the check correct no matter which render's closure fired.
      if (current && current.enabled === enabled) {
        return;
      }

      return this.$store.dispatch('notification_preferences/set_category', {
        category,
        enabled,
      });
    },

    /**
     * Re-read the OS permission state (IC3).
     *
     * Swallows failure on purpose. `push/refresh_permission` rejects if the
     * plugin is missing or the platform check throws, and the S4 contract for
     * that case is to render NOTHING -- a member on the website must not be
     * shown a failure because their browser has no push. Leaving `permission`
     * null is exactly that.
     */
    refresh_permission() {
      return this.$store.dispatch('push/refresh_permission').catch(() => null);
    },

    /**
     * The `prompt` variant's button. The only path in the app that reaches the
     * OS prompt from this screen.
     */
    ask_permission() {
      return this.$store.dispatch('push/request_permission_and_register')
        .catch(() => null)
        // Re-read afterwards so the banner updates in place: a member who
        // declines must see the denied variant without leaving the screen.
        .finally(() => this.refresh_permission());
    },

    /**
     * The `denied` variant's button.
     *
     * NOT awaited to sequence anything. On Android NativeSettings.open resolves
     * via startActivityForResult -- i.e. when the member CLOSES the settings
     * activity, possibly minutes later -- so treating it as "the member is back"
     * would hang the UI on a human. Coming back is handled by the appStateChange
     * listener instead.
     *
     * The web branch never reaches here: isPushSupported() being false renders
     * the web note, which has no button.
     */
    open_phone_settings() {
      return openOsNotificationSettings().then((opened) => {
        if (opened) {
          return;
        }

        // Exactly one message. openOsNotificationSettings never throws and never
        // toasts, so this is the only one.
        this.$store.dispatch('notifications/addDangerNotification', {
          message: SETTINGS_FAILED_MESSAGE,
        });
      });
    },
  },

  watch: {
    //
  }
};
</script>

<style>
/* --------------------------------------------------------------------------
 * S5/S6 long-text.
 *
 * Framework7 ships `--f7-list-item-text-max-lines: 2` and consumes it as
 * `-webkit-line-clamp`, so a third line is not wrapped-and-visible, it is
 * CLIPPED with an ellipsis. The longest description the server sends
 * (`operational`, 89 characters) needs three lines at 15px in the ~288px column
 * left beside a 52px toggle on a 320px screen, so at the default it would be
 * cut mid-sentence -- silently, with no error anywhere.
 *
 * Scoped to this list by class rather than raised globally: every other list in
 * the app is fine at 2 and this is not the page to change that on.
 * ------------------------------------------------------------------------ */
.notificaties-list {
  --f7-list-item-text-max-lines: 3;
}

/* Heading role, 16px/600 -- the same token f7-block-title uses, so the banner
   and failure headings line up with the group titles beside them. */
.notificaties-heading {
  font-size: var(--f7-block-title-font-size);
  font-weight: var(--f7-block-title-font-weight);
  line-height: var(--f7-block-title-line-height);
}

/* Body role, 15px/400 -- --f7-list-item-text-font-size, the same token the
   category descriptions beside it use.

   NOT --f7-list-font-size (17px, the category NAME token), which the UI-SPEC
   originally specified: at 17px the body renders LARGER than the 16px heading
   directly above it, which inverts the hierarchy on the one surface that only
   appears when something is wrong. Caught in review of 39-08; the UI-SPEC's
   Typography table was corrected to match rather than the other way round.

   Also not the f7-block inherited --f7-font-size (14px, the Label role) --
   that would make the banner read as a footnote rather than as the page's one
   message. 15px sits between: subordinate to its heading, louder than a
   footnote. */
.notificaties-body {
  font-size: var(--f7-list-item-text-font-size);
}

/* The banner is INFORMATIONAL, not an alert: the member's own choices are
   intact and the app is working, and they may have turned notifications off on
   purpose. So no amber, no red -- the same white surface as everything else,
   with the icon in the inherited secondary text colour. */
.notificaties-banner-icon {
  color: var(--f7-list-item-text-text-color);
}
</style>
